'use client';

import { useEffect, useSyncExternalStore } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { getConsentSnapshot, getServerConsentSnapshot, subscribeConsent } from '@/contexts/consent/store';
import { mixpanelClient } from '@/contexts/mixpanel/client';

/**
 * 由 App 决定挂载范围；只结算可见且已授权的 page_view/$duration。
 * 缺省按当前 URL（含 query）计页；App 可传入归一后的计页路径（如路由模板）。
 */
export function MixpanelPageTracker({ path: pagePath }: { /** 覆盖计页路径。 */ path?: string } = {}) {
    const { analyticsAllowed: enabled } = useSyncExternalStore(subscribeConsent, getConsentSnapshot, getServerConsentSnapshot);
    const { logger } = mixpanelClient;
    const pathname = usePathname();
    const search = useSearchParams()?.toString() ?? '';
    const path = pagePath ?? (search ? `${pathname}?${search}` : pathname);
    useEffect(() => {
        let startedAt: number | null = null;
        let active = true;
        const mountedAt = Date.now();
        // Strict Mode 的同步重挂载不代表真实曝光，微任务不延后授权期起点。
        queueMicrotask(() => {
            if (active && enabled && document.visibilityState !== 'hidden') startedAt = mountedAt;
        });
        const flush = () => {
            if (startedAt === null) return;
            const duration = Math.max(0, Date.now() - startedAt) / 1000;
            startedAt = null;
            logger('page_view', { path, $duration: duration });
        };
        const visibility = () => {
            if (document.visibilityState === 'hidden') flush();
            else if (enabled && startedAt === null) startedAt = Date.now();
        };
        window.addEventListener('pagehide', flush);
        document.addEventListener('visibilitychange', visibility);
        return () => {
            active = false;
            flush();
            window.removeEventListener('pagehide', flush);
            document.removeEventListener('visibilitychange', visibility);
        };
    }, [enabled, logger, path]);
    return null;
}
